'use client';

import { ReactElement, useEffect, useState } from 'react';
import { ArrowUp as ArrowUpIcon } from 'lucide-react';

import { classNames } from '@m1work/shared-utils';

import { IconButton } from '../IconButton/IconButton';

import styles from './ScrollToTopButton.module.scss';

const SCROLL_THRESHOLD = 400;

export interface ScrollToTopButtonProps {
    /** Accessible name, e.g. "Scroll to top" (translated). */
    label: string;
}

/**
 * A round button in the bottom-left corner that appears after scrolling down and scrolls the page back to the top.
 * Mount it once in the layout.
 *
 * @example
 * <ScrollToTopButton label={t('common.scroll-to-top')} />
 */
export function ScrollToTopButton({ label }: ScrollToTopButtonProps): ReactElement {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const handleScroll = (): void => setIsVisible(window.scrollY > SCROLL_THRESHOLD);
        handleScroll();
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToTop = (): void => window.scrollTo({ top: 0, behavior: 'smooth' });

    return (
        <IconButton
            aria-label={label}
            variant="fill"
            color="primary"
            rounded
            size="lg"
            className={classNames(styles.button, isVisible && styles.visible)}
            onClick={scrollToTop}
        >
            <ArrowUpIcon />
        </IconButton>
    );
}
